import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import Joyride, { CallBackProps, STATUS, Step } from "react-joyride";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { getTour, type TourRole } from "./tours";

interface WizardCtx {
  startTour: (role?: TourRole) => void;
  isActive: boolean;
}

const Ctx = createContext<WizardCtx | undefined>(undefined);

export function WizardProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [run, setRun] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);

  const startTour = useCallback(
    (roleOverride?: TourRole) => {
      const role = (roleOverride ?? (user?.role as TourRole | undefined)) ?? "RESIDENT";
      if (role !== "ADMIN" && role !== "BOARD_MEMBER" && role !== "RESIDENT") return;
      setSteps(getTour(role, i18n.language));
      setRun(true);
    },
    [user?.role, i18n.language]
  );

  const handleCallback = useCallback((data: CallBackProps) => {
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];
    if (finishedStatuses.includes(data.status)) {
      setRun(false);
      // Either outcome counts as "seen" — skipping is the user's choice to opt out,
      // not a reason for the system to forget it offered the tour.
      api.post("/users/me/onboarding-complete").catch(() => {
        // Non-critical: worst case the tour auto-starts again next login.
      });
    }
  }, []);

  const value = useMemo(() => ({ startTour, isActive: run }), [startTour, run]);

  const isEn = i18n.language.startsWith("en");
  const locale = {
    back: isEn ? "Back" : "Atrás",
    close: isEn ? "Close" : "Cerrar",
    last: isEn ? "Finish" : "Finalizar",
    next: isEn ? "Next" : "Siguiente",
    skip: isEn ? "Skip" : "Saltar",
  };
  // suppress unused warning
  void t;

  return (
    <Ctx.Provider value={value}>
      <Joyride
        steps={steps}
        run={run}
        continuous
        showSkipButton
        showProgress
        scrollToFirstStep
        disableScrolling={false}
        locale={locale}
        callback={handleCallback}
        styles={{
          options: {
            primaryColor: "#1e3a5f",
            zIndex: 10000,
          },
          tooltipTitle: {
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: 20,
            color: "#1e3a5f",
          },
        }}
      />
      {children}
    </Ctx.Provider>
  );
}

export function useWizard(): WizardCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useWizard must be used inside WizardProvider");
  return ctx;
}
